// plasma-research.js — research-grade plasma tabs:
//   Equilibrium (Soloviev), Ballooning (s-α), Mercier, Multi-mode,
//   Sensitivity, Convergence, EFIT import, Validation suite.
//
// 2D poloidal flux-surface renderer + s-α stability diagram canvas.

import init, {
  run_plasma_soloviev, run_plasma_ballooning, run_plasma_mercier,
  run_plasma_multi_mode, run_plasma_sensitivity, run_plasma_convergence,
  run_plasma_efit_parse, get_plasma_canonical_refs, run_plasma_validation
} from './engines.js';

let ready = false;
const _data = {};

export async function initResearch() {
  try {
    await init();
    ready = true;
  } catch (e) {
    console.error('plasma-research WASM init failed:', e);
  }
}

function timeit(fn) {
  const t0 = performance.now();
  const out = fn();
  const dt = performance.now() - t0;
  return { out, dt };
}

function setStatus(id, msg) {
  const el = document.getElementById(id);
  if (el) el.textContent = msg;
}

// ============================================================================
// 3D viewport adapter
// Maps each module's result to a window.gdbs3D payload { points, values, lines,
// title } and publishes it to window.__gdbs3dPayload. Guarded so nothing breaks
// when the shared viewport is absent. Does not touch physics or outputs.
// ============================================================================

// 2D N-by-N psi grid -> one point per cell, z = psi, coloured by psi.
function payloadFromGrid(sr, title) {
  const nz = sr.z_grid.length, nr = sr.r_grid.length;
  let pmin = Infinity, pmax = -Infinity;
  for (let j = 0; j < nz; j++) for (let i = 0; i < nr; i++) {
    const v = sr.psi[j][i];
    if (v < pmin) pmin = v;
    if (v > pmax) pmax = v;
  }
  const span = Math.max(1e-12, pmax - pmin);
  const r0 = sr.r_grid[0], r1 = sr.r_grid[nr - 1];
  const z0 = sr.z_grid[0], z1 = sr.z_grid[nz - 1];
  const rspan = Math.max(1e-12, r1 - r0), zspan = Math.max(1e-12, z1 - z0);
  const pts = [], values = [];
  for (let j = 0; j < nz; j++) for (let i = 0; i < nr; i++) {
    const x = (sr.r_grid[i] - r0) / rspan - 0.5;
    const y = (sr.z_grid[j] - z0) / zspan - 0.5;
    const v = sr.psi[j][i];
    pts.push(x, y, (v - pmin) / span);
    values.push(v);
  }
  return { points: new Float32Array(pts), values, title };
}

// Revolve a 1D radial/x profile (value vs radius) around the x axis into a
// disk/tube point cloud coloured by value.
function payloadFromProfile(xs, vals, title) {
  const n = xs.length; if (!n) return null;
  const x0 = xs[0], x1 = xs[n - 1];
  const xspan = Math.max(1e-12, x1 - x0);
  const maxAbs = Math.max(1e-12, ...vals.map(v => Math.abs(v)));
  const segs = 24;
  const pts = [], values = [];
  for (let i = 0; i < n; i++) {
    const ax = (xs[i] - x0) / xspan - 0.5;
    const r = Math.max(0, Math.abs(vals[i]) / maxAbs) * 0.5;
    for (let s = 0; s < segs; s++) {
      const th = (s / segs) * 2 * Math.PI;
      pts.push(ax, r * Math.cos(th), r * Math.sin(th));
      values.push(vals[i]);
    }
  }
  return { points: new Float32Array(pts), values, title };
}

// Multiple 1D curves sharing one x axis -> dense point cloud where each curve
// becomes a polyline; z = curve value, the series index spreads them along y so
// they do not overlap, coloured by value. Uses EVERY sample of every series.
function payloadFromMultiCurve(xs, series, title) {
  const n = xs.length; if (!n || !series.length) return null;
  const x0 = xs[0], x1 = xs[n - 1];
  const xspan = Math.max(1e-12, x1 - x0);
  let vlo = Infinity, vhi = -Infinity;
  for (const s of series) for (const v of s.vals) {
    if (v < vlo) vlo = v;
    if (v > vhi) vhi = v;
  }
  const vspan = Math.max(1e-12, vhi - vlo);
  const ns = series.length;
  const pts = [], values = [];
  for (let k = 0; k < ns; k++) {
    const vals = series[k].vals;
    const yk = ns > 1 ? (k / (ns - 1) - 0.5) : 0;
    for (let i = 0; i < n; i++) {
      const ax = (xs[i] - x0) / xspan - 0.5;
      const az = (vals[i] - vlo) / vspan - 0.5;
      pts.push(ax, yk, az);
      values.push(vals[i]);
    }
  }
  return { points: new Float32Array(pts), values, title };
}

// 2D scatter over an (x, y) grid, z = value, coloured by value.
function payloadFromScatter2D(xs, ys, vals, title) {
  const n = xs.length; if (!n) return null;
  const norm = (arr) => {
    const lo = Math.min(...arr), hi = Math.max(...arr);
    const sp = Math.max(1e-12, hi - lo);
    return (v) => (v - lo) / sp - 0.5;
  };
  const nx = norm(xs), ny = norm(ys), nv = norm(vals);
  const pts = [], values = [];
  for (let i = 0; i < n; i++) {
    pts.push(nx(xs[i]), ny(ys[i]), nv(vals[i]));
    values.push(vals[i]);
  }
  return { points: new Float32Array(pts), values, title };
}

// Genuinely-sparse multi-field scatter: map three numeric columns to x/y/z and a
// fourth to colour, using ALL items. Each column is per-axis normalized.
function payloadFromMultiField(items, xf, yf, zf, cf, title) {
  if (!items || !items.length) return null;
  const xs = items.map(xf), ys = items.map(yf), zs = items.map(zf);
  const cs = cf ? items.map(cf) : zs;
  const norm = (arr) => {
    const lo = Math.min(...arr), hi = Math.max(...arr);
    const sp = Math.max(1e-12, hi - lo);
    return (v) => (v - lo) / sp - 0.5;
  };
  const nx = norm(xs), ny = norm(ys), nz = norm(zs);
  const pts = [], values = [];
  for (let i = 0; i < items.length; i++) {
    pts.push(nx(xs[i]), ny(ys[i]), nz(zs[i]));
    values.push(cs[i]);
  }
  return { points: new Float32Array(pts), values, title };
}

function publish3D(payload) {
  if (payload) window.__gdbs3dPayload = payload;
}

// Add a small "View in 3D" button near the results status line, idempotent.
function add3DButton(statusId, getPayload, title) {
  if (!window.gdbs3D) return;
  const status = document.getElementById(statusId);
  const host = status ? status.parentElement : null;
  if (!host) return;
  let btn = host.querySelector('.pl-view3d-btn');
  if (!btn) {
    btn = document.createElement('button');
    btn.className = 'pl-view3d-btn';
    btn.type = 'button';
    btn.textContent = 'View in 3D';
    btn.style.cssText = 'margin-left:8px;padding:4px 12px;background:#0b2030;color:#4fc3f7;border:1px solid #1e3a5f;border-radius:4px;cursor:pointer;font-size:11px;';
    host.appendChild(btn);
  }
  btn.onclick = () => {
    const p = getPayload();
    publish3D(p);
    if (window.gdbs3D && window.gdbs3D.open && window.__gdbs3dPayload) window.gdbs3D.open(window.__gdbs3dPayload);
  };
}

// ============================================================================
// 1. Soloviev equilibrium + 2D poloidal renderer
// ============================================================================

export async function runSoloviev(form) {
  if (!ready) return;
  const req = {
    r0: parseFloat(form.r0.value),
    a: parseFloat(form.a.value),
    kappa: parseFloat(form.kappa.value),
    delta: parseFloat(form.delta.value),
    b0: parseFloat(form.b0.value),
    beta_p: parseFloat(form.beta_p.value),
    n_r: parseInt(form.n_r.value),
    n_z: parseInt(form.n_z.value),
  };
  if (window.gdbsShowLoading) {
    window.gdbsShowLoading('Soloviev equilibrium', 'Solving Grad-Shafranov...');
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  }
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_plasma_soloviev(JSON.stringify(req)));
    const elapsed = performance.now() - t0;
    setStatus('s-soloviev', `${elapsed.toFixed(1)} ms · I_p=${res.ip_ma.toFixed(2)} MA · β_N=${res.beta_n.toFixed(3)} · axis=(${res.axis_r.toFixed(3)}, ${res.axis_z.toFixed(3)})`);
    _data.soloviev = res;
    renderPoloidal('c-soloviev', res);
    renderProfile('c-soloviev-profile', res);
    const p3d = payloadFromGrid(res, 'Soloviev psi(R,Z)');
    publish3D(p3d);
    add3DButton('s-soloviev', () => payloadFromGrid(_data.soloviev, 'Soloviev psi(R,Z)'));
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
}

/**
 * 2D poloidal cross-section renderer.
 * Draws flux surfaces as contour lines, magnetic axis as cross,
 * plasma boundary highlighted. Mirrors the visual style of EFIT contour
 * plots used in publications.
 */
export function renderPoloidal(canvasId, sr) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W = canvas.width = canvas.clientWidth || 480;
  const H = canvas.height = canvas.clientHeight || 480;
  ctx.fillStyle = window.cpal().panel;
  ctx.fillRect(0, 0, W, H);

  const nz = sr.z_grid.length;
  const nr = sr.r_grid.length;
  const r_min = sr.r_grid[0];
  const r_max = sr.r_grid[nr - 1];
  const z_min = sr.z_grid[0];
  const z_max = sr.z_grid[nz - 1];

  const pad = 40;
  const sx = (W - 2 * pad) / (r_max - r_min);
  const sy = (H - 2 * pad) / (z_max - z_min);

  // Find ψ range for contour levels
  let pmin = Infinity, pmax = -Infinity;
  for (let j = 0; j < nz; j++) for (let i = 0; i < nr; i++) {
    if (sr.psi[j][i] < pmin) pmin = sr.psi[j][i];
    if (sr.psi[j][i] > pmax) pmax = sr.psi[j][i];
  }

  // Heat-map background: cool→warm by ψ
  const img = ctx.createImageData(W, H);
  for (let py = 0; py < H; py++) {
    for (let px = 0; px < W; px++) {
      const r = r_min + (px - pad) / sx;
      const z = z_max - (py - pad) / sy;
      if (r < r_min || r > r_max || z < z_min || z > z_max) {
        const idx = (py * W + px) * 4;
        img.data[idx] = 5; img.data[idx+1] = 16; img.data[idx+2] = 24; img.data[idx+3] = 255;
        continue;
      }
      const fi = (r - r_min) / (r_max - r_min) * (nr - 1);
      const fj = (z_max - z) / (z_max - z_min) * (nz - 1);
      const i0 = Math.floor(fi), j0 = Math.floor(fj);
      const i1 = Math.min(i0 + 1, nr - 1), j1 = Math.min(j0 + 1, nz - 1);
      const dx = fi - i0, dy = fj - j0;
      const psi = (1-dx)*(1-dy)*sr.psi[j0][i0] + dx*(1-dy)*sr.psi[j0][i1]
                + (1-dx)*dy*sr.psi[j1][i0] + dx*dy*sr.psi[j1][i1];
      const t = (psi - pmin) / (pmax - pmin);
      const r8 = Math.floor(40 + 100 * (1 - t));
      const g8 = Math.floor(80 + 100 * (1 - t * t));
      const b8 = Math.floor(120 + 80 * (1 - t));
      const idx = (py * W + px) * 4;
      img.data[idx] = r8; img.data[idx+1] = g8; img.data[idx+2] = b8; img.data[idx+3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  // Contour lines (marching-squares per surface level)
  const levels = [];
  const n_lev = 12;
  for (let k = 1; k < n_lev; k++) levels.push(pmin + (pmax - pmin) * k / n_lev);

  ctx.lineWidth = 1;
  for (const lv of levels) {
    ctx.strokeStyle = `rgba(180, 220, 255, 0.5)`;
    drawContour(ctx, sr.psi, sr.r_grid, sr.z_grid, lv, pad, sx, sy, z_max);
  }

  // Plasma boundary (separatrix-ish: highest level)
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#fbbf24';
  drawContour(ctx, sr.psi, sr.r_grid, sr.z_grid, pmin + (pmax - pmin) * 0.95, pad, sx, sy, z_max);

  // Magnetic axis cross
  const ax = pad + (sr.axis_r - r_min) * sx;
  const az = pad + (z_max - sr.axis_z) * sy;
  ctx.strokeStyle = '#ef4444';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(ax - 8, az); ctx.lineTo(ax + 8, az);
  ctx.moveTo(ax, az - 8); ctx.lineTo(ax, az + 8);
  ctx.stroke();

  // Axis labels
  ctx.fillStyle = window.cpal().text;
  ctx.font = '11px sans-serif';
  ctx.fillText(`R [m]`, W - 40, H - 10);
  ctx.fillText(`Z [m]`, 8, 16);
  ctx.fillText(r_min.toFixed(1), pad - 5, H - pad + 14);
  ctx.fillText(r_max.toFixed(1), W - pad - 18, H - pad + 14);
  ctx.fillText(z_max.toFixed(1), 8, pad + 4);
  ctx.fillText(z_min.toFixed(1), 8, H - pad - 2);
}

function drawContour(ctx, psi, r_grid, z_grid, level, pad, sx, sy, z_max) {
  const nz = z_grid.length;
  const nr = r_grid.length;
  ctx.beginPath();
  for (let j = 0; j < nz - 1; j++) {
    for (let i = 0; i < nr - 1; i++) {
      const v00 = psi[j][i], v01 = psi[j][i+1], v11 = psi[j+1][i+1], v10 = psi[j+1][i];
      const r0 = r_grid[i], r1 = r_grid[i+1], z0 = z_grid[j], z1 = z_grid[j+1];
      // 4 edges of cell; emit segment endpoints where the level crosses
      const pts = [];
      if ((v00 - level) * (v01 - level) < 0) {
        const t = (level - v00) / (v01 - v00);
        pts.push([r0 + t * (r1 - r0), z0]);
      }
      if ((v01 - level) * (v11 - level) < 0) {
        const t = (level - v01) / (v11 - v01);
        pts.push([r1, z0 + t * (z1 - z0)]);
      }
      if ((v11 - level) * (v10 - level) < 0) {
        const t = (level - v11) / (v10 - v11);
        pts.push([r1 + t * (r0 - r1), z1]);
      }
      if ((v10 - level) * (v00 - level) < 0) {
        const t = (level - v10) / (v00 - v10);
        pts.push([r0, z1 + t * (z0 - z1)]);
      }
      if (pts.length === 2) {
        ctx.moveTo(pad + (pts[0][0] - r_grid[0]) * sx, pad + (z_max - pts[0][1]) * sy);
        ctx.lineTo(pad + (pts[1][0] - r_grid[0]) * sx, pad + (z_max - pts[1][1]) * sy);
      }
    }
  }
  ctx.stroke();
}

function renderProfile(canvasId, sr) {
  const canvas = document.getElementById(canvasId);
  if (!canvas || !window.Chart) return;
  if (canvas._chart) canvas._chart.destroy();
  const psi_n = sr.p_profile.map((_, i) => i / (sr.p_profile.length - 1));
  canvas._chart = new Chart(canvas.getContext('2d'), {
    type: 'line',
    data: {
      labels: psi_n.map(v => v.toFixed(2)),
      datasets: [
        { label: 'p(ψ_n) [Pa]', data: sr.p_profile, borderColor: '#fbbf24', yAxisID: 'yL', fill: false },
        { label: 'q(ψ_n)', data: sr.q_profile, borderColor: '#4fc3f7', yAxisID: 'yR', fill: false },
      ]
    },
    options: {
      responsive: true, maintainAspectRatio: false, animation: false,
      scales: {
        x: { title: { display: true, text: 'normalized ψ' } },
        yL: { type: 'linear', position: 'left', title: { display: true, text: 'pressure [Pa]' } },
        yR: { type: 'linear', position: 'right', title: { display: true, text: 'q' }, grid: { drawOnChartArea: false } },
      },
    }
  });
}

// ============================================================================
// 2. Ballooning s-α
// ============================================================================

export async function runBallooning(form) {
  if (!ready) return;
  const s_min = parseFloat(form.s_min.value), s_max = parseFloat(form.s_max.value);
  const a_min = parseFloat(form.alpha_min.value), a_max = parseFloat(form.alpha_max.value);
  const n_s = parseInt(form.n_s.value), n_a = parseInt(form.n_a.value);
  const linspace = (lo, hi, n) => Array.from({length: n}, (_, i) => lo + (hi - lo) * i / Math.max(1, n - 1));
  const req = {
    s_values: linspace(s_min, s_max, n_s),
    alpha_values: linspace(a_min, a_max, n_a),
    n_theta: parseInt(form.n_theta.value),
    theta_max: parseFloat(form.theta_max.value),
  };
  if (window.gdbsShowLoading) {
    window.gdbsShowLoading('Ballooning s-alpha scan', 'Scanning stability grid...');
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  }
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_plasma_ballooning(JSON.stringify(req)));
    const dt = performance.now() - t0;
    setStatus('s-balloon', `${dt.toFixed(1)} ms · ${res.points.length} grid points · α_crit_max = ${res.first_stability_alpha_max.toFixed(2)}`);
    _data.ballooning = res;
    renderSAlpha('c-balloon', res, req.s_values, req.alpha_values);
    const ball3d = () => {
      const sVals = req.s_values, aVals = req.alpha_values;
      const nA = aVals.length;
      const xs = [], ys = [], vals = [];
      for (let i = 0; i < sVals.length; i++) for (let j = 0; j < nA; j++) {
        const pt = res.points[i * nA + j];
        if (!pt) continue;
        xs.push(sVals[i]); ys.push(aVals[j]); vals.push(pt.eigenvalue);
      }
      return payloadFromScatter2D(xs, ys, vals, 'Ballooning eigenvalue(s, alpha)');
    };
    publish3D(ball3d());
    add3DButton('s-balloon', ball3d);
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
}

export function renderSAlpha(canvasId, res, sVals, aVals) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W = canvas.width = canvas.clientWidth || 480;
  const H = canvas.height = canvas.clientHeight || 360;
  ctx.fillStyle = window.cpal().panel;
  ctx.fillRect(0, 0, W, H);

  const pad = 50;
  const s_lo = sVals[0], s_hi = sVals[sVals.length - 1];
  const a_lo = aVals[0], a_hi = aVals[aVals.length - 1];
  const sx = (W - 2 * pad) / (s_hi - s_lo);
  const sy = (H - 2 * pad) / (a_hi - a_lo);

  // Heatmap of growth rate
  const nS = sVals.length, nA = aVals.length;
  const cellW = (W - 2 * pad) / nS;
  const cellH = (H - 2 * pad) / nA;
  let lam_min = Infinity, lam_max = -Infinity;
  for (const p of res.points) {
    if (p.eigenvalue < lam_min) lam_min = p.eigenvalue;
    if (p.eigenvalue > lam_max) lam_max = p.eigenvalue;
  }
  for (let i = 0; i < nS; i++) {
    for (let j = 0; j < nA; j++) {
      const pt = res.points[i * nA + j];
      const t = (pt.eigenvalue - lam_min) / Math.max(1e-12, lam_max - lam_min);
      // unstable (low λ) → red; stable (high λ) → blue
      const r = Math.floor(220 * (1 - t)), g = Math.floor(80 * t), b = Math.floor(180 * t);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(pad + i * cellW, H - pad - (j + 1) * cellH, cellW + 1, cellH + 1);
    }
  }

  // Boundary curve
  ctx.strokeStyle = '#fbbf24';
  ctx.lineWidth = 2;
  ctx.beginPath();
  let first = true;
  for (const [s, a] of res.stability_boundary) {
    const x = pad + (s - s_lo) * sx;
    const y = H - pad - (a - a_lo) * sy;
    if (first) { ctx.moveTo(x, y); first = false; } else ctx.lineTo(x, y);
  }
  ctx.stroke();

  // Axes
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1;
  ctx.strokeRect(pad, pad, W - 2 * pad, H - 2 * pad);
  ctx.fillStyle = window.cpal().strong;
  ctx.font = '12px sans-serif';
  ctx.fillText('s (magnetic shear)', W / 2 - 50, H - 15);
  ctx.save();
  ctx.translate(15, H / 2 + 50);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText('α (pressure gradient)', 0, 0);
  ctx.restore();
  ctx.font = '10px sans-serif';
  ctx.fillText(s_lo.toFixed(1), pad - 5, H - pad + 14);
  ctx.fillText(s_hi.toFixed(1), W - pad - 12, H - pad + 14);
  ctx.fillText(a_lo.toFixed(1), 28, H - pad + 4);
  ctx.fillText(a_hi.toFixed(1), 28, pad + 4);

  // Legend
  ctx.fillStyle = '#fbbf24';
  ctx.fillText('— stability boundary', pad + 5, pad + 14);
  ctx.fillStyle = '#fca5a5';
  ctx.fillText('■ unstable', pad + 5, pad + 28);
  ctx.fillStyle = '#93c5fd';
  ctx.fillText('■ stable', pad + 5, pad + 42);
}

// ============================================================================
// 3. Mercier
// ============================================================================

export async function runMercier(form) {
  if (!ready) return;
  const req = {
    config_type: form.config_type.value,
    aspect_ratio: parseFloat(form.aspect_ratio.value),
    beta_p: parseFloat(form.beta_p.value),
    kappa: parseFloat(form.kappa.value),
    delta: parseFloat(form.delta.value),
    iota0: parseFloat(form.iota0.value),
    iota_edge: parseFloat(form.iota_edge.value),
    well_depth: parseFloat(form.well_depth.value),
    n_radial: parseInt(form.n_radial.value),
    constants: { b0: 5.0, q0: 1.0, qa: 3.5, n_radial: parseInt(form.n_radial.value),
                 m_mode: 2.0, n_mode: 1.0, beta_n_target: 3.0 },
  };
  if (window.gdbsShowLoading) {
    window.gdbsShowLoading('Mercier criterion', 'Evaluating D_M(s)...');
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  }
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_plasma_mercier(JSON.stringify(req)));
    const dt = performance.now() - t0;
    setStatus('s-mercier', `${dt.toFixed(1)} ms · D_max=${res.d_max.toFixed(4)} @ s=${res.s_at_d_max.toFixed(3)} · stable fraction=${(res.fraction_stable*100).toFixed(1)}%`);
    _data.mercier = res;
    const merc3d = () => {
      const pts = _data.mercier.points;
      const xs = pts.map(p => p.s);
      const series = [
        { vals: pts.map(p => p.d_mercier) },
        { vals: pts.map(p => p.d_well) },
        { vals: pts.map(p => p.d_shear) },
        { vals: pts.map(p => p.d_curr) },
      ];
      return payloadFromMultiCurve(xs, series, 'Mercier D_M(s): total / well / shear / curr');
    };
    publish3D(merc3d());
    add3DButton('s-mercier', merc3d);
    if (window.Chart) {
      const canvas = document.getElementById('c-mercier');
      if (canvas._chart) canvas._chart.destroy();
      canvas._chart = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels: res.points.map(p => p.s.toFixed(3)),
          datasets: [
            { label: 'D_M (total)', data: res.points.map(p => p.d_mercier), borderColor: '#fbbf24', fill: false },
            { label: 'D_well', data: res.points.map(p => p.d_well), borderColor: '#16a34a', fill: false },
            { label: 'D_shear', data: res.points.map(p => p.d_shear), borderColor: '#4fc3f7', fill: false },
            { label: 'D_curr', data: res.points.map(p => p.d_curr), borderColor: '#ef4444', fill: false },
            { label: 'stability threshold (0.25)', data: res.points.map(() => 0.25), borderColor: '#94a3b8', borderDash: [4,4], fill: false },
          ],
        },
        options: { responsive: true, maintainAspectRatio: false, animation: false,
          scales: { x: { title: { display: true, text: 'normalized radius s' } },
                    y: { title: { display: true, text: 'D_M' } } } }
      });
    }
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
}

// ============================================================================
// 4. Multi-mode δW
// ============================================================================

export async function runMultiMode(form) {
  if (!ready) return;
  const req = {
    constants: { b0: 5.0, q0: 1.0, qa: parseFloat(form.qa.value), n_radial: 256,
                 m_mode: 2.0, n_mode: 1.0, beta_n_target: 3.0 },
    aspect_ratio: parseFloat(form.aspect_ratio.value),
    beta: parseFloat(form.beta.value),
    m_min: parseInt(form.m_min.value),
    m_max: parseInt(form.m_max.value),
    n_min: parseInt(form.n_min.value),
    n_max: parseInt(form.n_max.value),
  };
  if (window.gdbsShowLoading) {
    window.gdbsShowLoading('Multi-mode delta_W scan', 'Scanning (m, n) modes...');
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  }
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_plasma_multi_mode(JSON.stringify(req)));
    const dt = performance.now() - t0;
    const dom = res.dominant ? `${res.dominant.m}/${res.dominant.n} (δW=${res.dominant.delta_w.toExponential(3)})` : 'all stable';
    setStatus('s-multimode', `${dt.toFixed(1)} ms · ${res.n_unstable}/${res.modes.length} unstable · dominant: ${dom}`);
    _data.multimode = res;
    const mm3d = () => payloadFromMultiField(
      _data.multimode.modes,
      m => m.m, m => m.n, m => m.delta_w, m => m.growth_rate_norm,
      'Multi-mode delta_W(m, n), colour = growth rate');
    publish3D(mm3d());
    add3DButton('s-multimode', mm3d);

    // Mode table
    const table = document.getElementById('t-multimode');
    if (table) {
      const rows = res.modes.slice().sort((a,b) => a.delta_w - b.delta_w).slice(0, 12);
      table.innerHTML = `<thead><tr><th>m/n</th><th>δW</th><th>q_res</th><th>s_res</th><th>γ_norm</th><th>stable</th></tr></thead><tbody>` +
        rows.map(m => `<tr>
          <td>${m.m}/${m.n}</td>
          <td>${m.delta_w.toExponential(3)}</td>
          <td>${m.q_resonant.toFixed(3)}</td>
          <td>${m.s_resonant < 0 ? '—' : m.s_resonant.toFixed(3)}</td>
          <td>${m.growth_rate_norm.toExponential(2)}</td>
          <td style="color:${m.stable ? '#6ee7b7' : '#fca5a5'};">${m.stable ? '✓' : '✗'}</td>
        </tr>`).join('') + '</tbody>';
    }
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
}

// ============================================================================
// 5. Sensitivity
// ============================================================================

export async function runSensitivity(form) {
  if (!ready) return;
  const req = {
    config_type: form.config_type.value,
    aspect_ratio: parseFloat(form.aspect_ratio.value),
    kappa: parseFloat(form.kappa.value),
    delta: parseFloat(form.delta.value),
    iota0: parseFloat(form.iota0.value),
    iota_edge: parseFloat(form.iota_edge.value),
    well_depth: parseFloat(form.well_depth.value),
    constants: { b0: 5.0, q0: 1.0, qa: parseFloat(form.qa.value), n_radial: 256,
                 m_mode: 2.0, n_mode: 1.0, beta_n_target: 3.0 },
  };
  if (window.gdbsShowLoading) {
    window.gdbsShowLoading('Sensitivity analysis', 'Computing elasticities...');
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  }
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_plasma_sensitivity(JSON.stringify(req)));
    const dt = performance.now() - t0;
    setStatus('s-sens', `${dt.toFixed(1)} ms · baseline β_N=${res.baseline_beta_n.toFixed(4)} · dominant: ${res.dominant_parameter}`);
    _data.sensitivity = res;
    // Genuinely a handful of per-parameter scalars (no field/curve): keep a
    // sparse multi-field scatter using all numeric columns as independent axes.
    const sens3d = () => payloadFromMultiField(
      _data.sensitivity.entries,
      e => e.rank, (e, i) => i, e => e.elasticity, e => e.elasticity,
      'Sensitivity: rank vs elasticity by parameter');
    publish3D(sens3d());
    add3DButton('s-sens', sens3d);

    if (window.Chart) {
      const canvas = document.getElementById('c-sens');
      if (canvas._chart) canvas._chart.destroy();
      canvas._chart = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: res.entries.map(e => `${e.parameter}\n(rank ${e.rank})`),
          datasets: [{
            label: 'elasticity (∂β_N/∂x · x/β_N)',
            data: res.entries.map(e => e.elasticity),
            backgroundColor: res.entries.map(e => e.elasticity > 0 ? '#16a34a' : '#ef4444'),
          }]
        },
        options: { responsive: true, maintainAspectRatio: false, animation: false,
          scales: { y: { title: { display: true, text: 'elasticity (dimensionless)' } } } }
      });
    }
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
}

// ============================================================================
// 6. Convergence study
// ============================================================================

export async function runConvergence(form) {
  if (!ready) return;
  const n_vals = form.n_values.value.split(',').map(v => parseInt(v.trim())).filter(v => v > 0);
  const req = {
    config_type: form.config_type.value,
    aspect_ratio: parseFloat(form.aspect_ratio.value),
    kappa: parseFloat(form.kappa.value),
    delta: parseFloat(form.delta.value),
    iota0: parseFloat(form.iota0.value),
    iota_edge: parseFloat(form.iota_edge.value),
    well_depth: parseFloat(form.well_depth.value),
    n_radial_values: n_vals,
    constants: { b0: 5.0, q0: 1.0, qa: 3.5, n_radial: 256,
                 m_mode: 2.0, n_mode: 1.0, beta_n_target: 3.0 },
  };
  if (window.gdbsShowLoading) {
    window.gdbsShowLoading('Convergence study', 'Running grid refinement...');
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  }
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_plasma_convergence(JSON.stringify(req)));
    const dt = performance.now() - t0;
    setStatus('s-conv', `${dt.toFixed(1)} ms · Richardson β_N=${res.richardson_value.toFixed(6)} · order=${res.estimated_order.toFixed(2)} · recommended n=${res.recommended_n}`);
    _data.convergence = res;
    const conv3d = () => {
      const pts = _data.convergence.points;
      const xs = pts.map(p => p.n_radial);
      const series = [
        { vals: pts.map(p => p.beta_n) },
        { vals: pts.map(p => Math.log10(Math.max(1e-30, Math.abs(p.rel_error)))) },
      ];
      return payloadFromMultiCurve(xs, series, 'Convergence beta_N(n) and log10|rel error|');
    };
    publish3D(conv3d());
    add3DButton('s-conv', conv3d);

    if (window.Chart) {
      const canvas = document.getElementById('c-conv');
      if (canvas._chart) canvas._chart.destroy();
      canvas._chart = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels: res.points.map(p => p.n_radial.toString()),
          datasets: [
            { label: 'β_N(n)', data: res.points.map(p => p.beta_n), borderColor: '#4fc3f7', yAxisID: 'yL', fill: false },
            { label: 'rel. error', data: res.points.map(p => p.rel_error), borderColor: '#ef4444', yAxisID: 'yR', fill: false },
          ]
        },
        options: { responsive: true, maintainAspectRatio: false, animation: false,
          scales: { x: { title: { display: true, text: 'n_radial' } },
                    yL: { type: 'linear', position: 'left', title: { display: true, text: 'β_N' } },
                    yR: { type: 'logarithmic', position: 'right', title: { display: true, text: 'rel error' }, grid: { drawOnChartArea: false } } } }
      });
    }
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
}

// ============================================================================
// 7. EFIT import
// ============================================================================

export function importEFIT(file) {
  if (!ready) return Promise.resolve();
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
      const content = e.target.result;
      const t0 = performance.now();
      const res = JSON.parse(run_plasma_efit_parse(content));
      const dt = performance.now() - t0;
      const statusEl = document.getElementById('s-efit');
      if (res.success) {
        statusEl.textContent = `${dt.toFixed(1)} ms · loaded ${res.data.nw}×${res.data.nh} grid · I_p=${(res.data.cpasma/1e6).toFixed(2)} MA · ${res.data.header}`;
        _data.efit = res.data;
        const fakeSr = {
          r_grid: res.data.r_grid, z_grid: res.data.z_grid, psi: res.data.psi_rz,
          axis_r: res.data.rmagx, axis_z: res.data.zmagx,
          p_profile: res.data.pres, q_profile: res.data.q_profile,
        };
        renderPoloidal('c-efit', fakeSr);
        const efit3d = () => payloadFromGrid(fakeSr, `EFIT psi(R,Z) ${res.data.nw}x${res.data.nh}`);
        publish3D(efit3d());
        add3DButton('s-efit', efit3d);
      } else {
        statusEl.textContent = `Parse failed: ${res.error}`;
      }
      resolve(res);
    };
    reader.readAsText(file);
  });
}

// ============================================================================
// 8. Validation suite
// ============================================================================

export async function loadValidationRefs() {
  if (!ready) return [];
  const refs = JSON.parse(get_plasma_canonical_refs());
  return refs;
}

export function runValidation(refId) {
  if (!ready) return null;
  const t0 = performance.now();
  const res = JSON.parse(run_plasma_validation(refId));
  const dt = performance.now() - t0;
  return { ...res, elapsed_ms: dt };
}

/// Render a single row's verdict, given a validation result + the ref.
function renderVerdict(r, v) {
  // Stellarator and FRC references don't use β_N (no toroidal current).
  // Our current Validation solver computes a tokamak β_N which would
  // give a meaningless error for those categories - mark Referenced until the
  // stellarator/FRC-specific validators land.
  const usesBetaN = r.category === 'tokamak' || r.category === 'theory';
  if (!usesBetaN) {
    return {
      computed: '—',
      verdict: 'Referenced · β_t not β_N',
      color: '#94a3b8',
      tooltip: `${r.category} references compare against ⟨β_t⟩, which requires the ${r.category}-specific solver. Tokamak β_N solver is not applicable here.`,
    };
  }
  if (!v) {
    return { computed: '—', verdict: 'Error', color: '#fca5a5', tooltip: 'Solver did not return a result.' };
  }
  const passed = v.within_tolerance;
  return {
    computed: v.computed_beta_n.toFixed(3),
    verdict: passed
      ? `Valid · β_N=${v.computed_beta_n.toFixed(2)} (Troyon-consistent)`
      : `β_N=${v.computed_beta_n.toFixed(2)} (outside Troyon band)`,
    color: passed ? '#6ee7b7' : '#fbbf24',
    tooltip: (v.status || `ideal-MHD no-wall β_N limit = ${v.computed_beta_n.toFixed(2)} (Troyon-scale)`) + ` Operational design point ${r.expected_beta_n.toFixed(2)} shown for context, not tuned to.`,
  };
}

function runOneValidation(r) {
  if (r.category !== 'tokamak' && r.category !== 'theory') return null;
  return runValidation(r.id);
}

// Build a sparse multi-field scatter from all validation references that
// produced a computed beta_N: expected vs computed vs error, colour = error.
function publishValidation3D(rows) {
  const items = rows.filter(x => x.v && typeof x.v.computed_beta_n === 'number');
  if (!items.length) return;
  const p = payloadFromMultiField(
    items,
    x => x.r.expected_beta_n, x => x.v.computed_beta_n, x => x.v.error_pct, x => x.v.error_pct,
    'Validation: expected vs computed beta_N, colour = error %');
  publish3D(p);
}

function paintValidationRow(r) {
  const v = runOneValidation(r);
  const vd = renderVerdict(r, v);
  const cc = document.getElementById('vc-' + r.id);
  const vr = document.getElementById('vr-' + r.id);
  if (cc) cc.textContent = vd.computed;
  if (vr) {
    const failed = v && !v.within_tolerance && (r.category === 'tokamak' || r.category === 'theory');
    const reportLink = failed
      ? ` <a href="javascript:void(0)" data-validation-fail="${r.id}" style="color:#fbbf24;text-decoration:none;font-size:10px;margin-left:6px;border:1px solid #4a3009;padding:1px 6px;border-radius:3px;" title="Report this failure as a support ticket">Report</a>`
      : '';
    vr.innerHTML = `<span title="${vd.tooltip.replace(/"/g, '&quot;')}">${vd.verdict}</span>${reportLink}`;
    vr.style.color = vd.color;
    if (failed) {
      const link = vr.querySelector('[data-validation-fail]');
      link?.addEventListener('click', () => openValidationFailTicket(r, v));
    }
  }
  return { r, v };
}

function openValidationFailTicket(r, v) {
  const body =
`Validation reference "${r.name}" (id: ${r.id}) FAILED.

Solver output:
  computed β_N (calibrated): ${v.computed_beta_n.toFixed(4)}
  computed β_N (raw trial-function δW): ${v.computed_beta_n_raw.toFixed(4)}
  substrate calibration: ${v.substrate_numerator}/${v.substrate_denominator} = ${v.substrate_factor.toFixed(4)}
  expected β_N: ${r.expected_beta_n.toFixed(3)}
  error: ${v.error_pct.toFixed(2)}%
  tolerance band: ±${r.tolerance_pct.toFixed(0)}%

Reference citation: ${r.source}
DOI / URL: ${r.url}

Geometry:
  R0=${r.r0} m, a=${r.a} m, κ=${r.kappa}, δ=${r.delta}, B0=${r.b0} T, q_edge=${r.q_edge}

What to investigate:
- Is the published β_N the operational design point or the theoretical limit?
- Does this scenario need additional physics (RWM, bootstrap self-consistency,
  pedestal model, peeling-ballooning) not currently in the shaped solver?
- Is the substrate calibration ${v.substrate_numerator}/${v.substrate_denominator} still appropriate, or has the
  reference value been updated in a newer paper?
`;
  window.gdbsOpenTicketWithContext?.({
    module: 'plasma',
    category: 'physics',
    priority: 'P3',
    subject: `Validation FAIL: ${r.name} (${v.error_pct.toFixed(1)}%)`,
    body,
    context: {
      validation_ref: r.id,
      validation_computed: v.computed_beta_n,
      validation_expected: r.expected_beta_n,
      validation_error_pct: v.error_pct,
      validation_substrate: `${v.substrate_numerator}/${v.substrate_denominator}`,
    },
  });
}

export async function renderValidationTable() {
  const refs = await loadValidationRefs();
  const tbody = document.getElementById('t-validation');
  if (!tbody) return;
  tbody.innerHTML = '';
  for (const r of refs) {
    const row = document.createElement('tr');
    row.innerHTML = `
      <td>${r.name}</td>
      <td>${r.category}</td>
      <td>${r.expected_beta_n.toFixed(2)}</td>
      <td id="vc-${r.id}">—</td>
      <td id="vr-${r.id}" style="color:#94a3b8;">pending…</td>
      <td><a href="${r.url}" target="_blank" style="color:#4fc3f7;">${r.source}</a></td>`;
    tbody.appendChild(row);
  }
  // Auto-run all references — no per-row click needed.
  setTimeout(() => {
    const rows = refs.map(r => paintValidationRow(r));
    publishValidation3D(rows);
  }, 0);
}

window.gdbsRerunAllValidation = async function() {
  const refs = await loadValidationRefs();
  for (const r of refs) {
    const vr = document.getElementById('vr-' + r.id);
    if (vr) { vr.textContent = 'running…'; vr.style.color = '#94a3b8'; }
  }
  // Yield so UI updates before the synchronous solver runs.
  await new Promise(r => setTimeout(r, 16));
  const rows = refs.map(r => paintValidationRow(r));
  publishValidation3D(rows);
};

export { _data };
